import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { useGlobals } from "../../hooks/useGlobals";

function HomePage() {
  const { t } = useTranslation();
  const { authMember } = useGlobals();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-cyan-50 to-blue-100 text-slate-900 py-32 px-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute top-10 right-10 w-72 h-72 bg-cyan-300 opacity-5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-300 opacity-3 rounded-full blur-3xl"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            {/* Logo */}
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl flex items-center justify-center shadow-2xl">
                <svg
                  className="w-12 h-12 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
            </div>

            <h1 className="text-6xl md:text-7xl font-black mb-6 leading-tight text-slate-900">
              {t("home.hero.title")}
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto mb-12 leading-relaxed font-light">
              {t("home.hero.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to={authMember ? "/my-page" : "/practice"}
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-10 py-4 rounded-xl font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-cyan-300/50 transform hover:scale-105"
              >
                {authMember
                  ? t("home.hero.ctaPrimaryLoggedIn")
                  : t("home.hero.ctaPrimary")}
              </Link>
              {!authMember && (
                <Link
                  to="/login"
                  className="bg-white border-2 border-cyan-400 text-cyan-600 px-10 py-4 rounded-xl font-bold text-lg hover:bg-cyan-50 transition-all shadow-md hover:shadow-cyan-200/50 transform hover:scale-105"
                >
                  {t("home.hero.ctaSecondary")}
                </Link>
              )}
            </div>
          </div>

          {/* Hero Stats */}
          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {/* <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 border border-cyan-200 border-opacity-50 text-center hover:bg-opacity-80 transition-all shadow-sm hover:shadow-lg">
              <div className="text-5xl font-black mb-3 bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                10K+
              </div>
              <div className="text-slate-600 font-medium">
                {t("home.hero.stat1")}
              </div>
            </div> */}

            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 border border-blue-200 border-opacity-50 text-center hover:bg-opacity-80 transition-all shadow-sm hover:shadow-lg">
              <div className="text-5xl font-black mb-3 bg-gradient-to-r from-blue-500 to-cyan-500 bg-clip-text text-transparent">
                500K+
              </div>
              <div className="text-slate-600 font-medium">
                {t("home.hero.stat2")}
              </div>
            </div>
            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 border border-cyan-200 border-opacity-50 text-center hover:bg-opacity-80 transition-all shadow-sm hover:shadow-lg">
              <div className="text-5xl font-black mb-3 bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                95%
              </div>
              <div className="text-slate-600 font-medium">
                {t("home.hero.stat3")}
              </div>
            </div>

            <div className="bg-white bg-opacity-60 backdrop-blur-sm rounded-2xl p-8 border border-cyan-200 border-opacity-50 text-center hover:bg-opacity-80 transition-all shadow-sm hover:shadow-lg">
              <div className="text-5xl font-black mb-3 bg-gradient-to-r from-cyan-500 to-blue-500 bg-clip-text text-transparent">
                3+
              </div>
              <div className="text-slate-600 font-medium">
                {t("home.hero.stat4")}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-32 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
              {t("home.about.title")}
            </h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-light">
              {t("home.about.description")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Card 1 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              <div className="bg-white rounded-2xl p-8 hover:shadow-xl shadow-md transition-all duration-300 border border-cyan-100 h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {t("home.about.card1.title")}
                </h3>
                <p className="text-slate-600 leading-relaxed font-light">
                  {t("home.about.card1.description")}
                </p>
              </div>
            </div>

            {/* Card 2 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              <div className="bg-white rounded-2xl p-8 hover:shadow-xl shadow-md transition-all duration-300 border border-blue-100 h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {t("home.about.card2.title")}
                </h3>
                <p className="text-slate-600 leading-relaxed font-light">
                  {t("home.about.card2.description")}
                </p>
              </div>
            </div>

            {/* Card 3 */}
            <div className="group relative">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-200 to-blue-200 rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
              <div className="bg-white rounded-2xl p-8 hover:shadow-xl shadow-md transition-all duration-300 border border-cyan-100 h-full">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center mb-6 shadow-lg group-hover:scale-110 transition-transform">
                  <svg
                    className="w-8 h-8 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  {t("home.about.card3.title")}
                </h3>
                <p className="text-slate-600 leading-relaxed font-light">
                  {t("home.about.card3.description")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-32 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
              {t("home.features.title")}
            </h2>
            <div className="w-24 h-1.5 bg-gradient-to-r from-cyan-400 to-blue-500 mx-auto mb-8 rounded-full"></div>
            <p className="text-xl text-slate-600 max-w-4xl mx-auto leading-relaxed font-light">
              {t("home.features.description")}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* Feature 1 */}
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:shadow-cyan-200/30 transition-all duration-300 border border-cyan-100 group">
              <div className="flex items-start gap-6">
                <div className="shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {t("home.features.feature1.title")}
                  </h3>
                  <p className="text-slate-600 leading-relaxed font-light">
                    {t("home.features.feature1.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 2 */}
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:shadow-blue-200/30 transition-all duration-300 border border-blue-100 group">
              <div className="flex items-start gap-6">
                <div className="shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {t("home.features.feature2.title")}
                  </h3>
                  <p className="text-slate-600 leading-relaxed font-light">
                    {t("home.features.feature2.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 3 */}
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:shadow-cyan-200/30 transition-all duration-300 border border-cyan-100 group">
              <div className="flex items-start gap-6">
                <div className="shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {t("home.features.feature3.title")}
                  </h3>
                  <p className="text-slate-600 leading-relaxed font-light">
                    {t("home.features.feature3.description")}
                  </p>
                </div>
              </div>
            </div>

            {/* Feature 4 */}
            <div className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl hover:shadow-blue-200/30 transition-all duration-300 border border-blue-100 group">
              <div className="flex items-start gap-6">
                <div className="shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
                    <svg
                      className="w-8 h-8 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                      />
                    </svg>
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-slate-900 mb-3">
                    {t("home.features.feature4.title")}
                  </h3>
                  <p className="text-slate-600 leading-relaxed font-light">
                    {t("home.features.feature4.description")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center">
            <Link
              to="/practice"
              className="inline-block bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-4 rounded-xl font-bold text-lg hover:shadow-lg hover:shadow-cyan-300/40 transition-all shadow-md hover:scale-105 transform"
            >
              {t("home.features.cta")}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      {!authMember && (
        <section className="py-32 bg-gradient-to-br from-blue-100 via-cyan-50 to-blue-50 text-slate-900 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-cyan-300 opacity-5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-blue-300 opacity-3 rounded-full blur-3xl"></div>

          <div className="max-w-5xl mx-auto px-4 text-center relative z-10">
            <h2 className="text-5xl md:text-6xl font-black mb-8 leading-tight text-slate-900">
              {t("home.cta.title")}
            </h2>
            <p className="text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed font-light">
              {t("home.cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/login"
                className="bg-gradient-to-r from-cyan-500 to-blue-500 text-white px-12 py-4 rounded-xl font-bold text-lg hover:from-cyan-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-cyan-300/50 transform hover:scale-105"
              >
                {t("home.cta.primaryBtn")}
              </Link>
              <Link
                to="/practice"
                className="bg-white border-2 border-cyan-400 text-cyan-600 px-12 py-4 rounded-xl font-bold text-lg hover:bg-cyan-50 transition-all shadow-md hover:shadow-cyan-200/50 transform hover:scale-105"
              >
                {t("home.cta.secondaryBtn")}
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-slate-100 text-slate-700 py-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <Link to="/" className="flex items-center group mb-4">
                <img
                  src="/logo-crop.png"
                  alt="Intervify Logo"
                  style={{ height: "150px", width: "auto" }}
                  className="h-16 w-auto object-contain group-hover:opacity-80 transition-opacity duration-300"
                />
              </Link>
              <p className="text-slate-600 leading-relaxed font-light">
                {t("home.footer.description")}
              </p>
            </div>

            {/* Links */}
            <div>
              <h3 className="text-slate-900 font-bold text-lg mb-6">
                {t("home.footer.quickLinks")}
              </h3>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-slate-600 hover:text-cyan-600 transition-colors font-medium"
                  >
                    {t("home.footer.home")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/practice"
                    className="text-slate-600 hover:text-cyan-600 transition-colors font-medium"
                  >
                    {t("home.footer.practice")}
                  </Link>
                </li>
                {!authMember && (
                  <li>
                    <Link
                      to="/login"
                      className="text-slate-600 hover:text-cyan-600 transition-colors font-medium"
                    >
                      {t("home.footer.login")}
                    </Link>
                  </li>
                )}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h3 className="text-slate-900 font-bold text-lg mb-6">
                {t("home.footer.contact")}
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-cyan-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                  <span className="font-medium text-slate-700">
                    {t("home.footer.email")}
                  </span>
                </li>
                {/* <li className="flex items-center gap-3">
                  <svg
                    className="w-5 h-5 text-cyan-500"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                  <span className="font-medium text-slate-700">
                    {t("home.footer.phone")}
                  </span>
                </li> */}
              </ul>
            </div>
          </div>

          <div className="border-t border-slate-300 pt-8 text-center text-slate-600 font-medium">
            <p>{t("home.footer.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default HomePage;
